import {
  SCREEN_HOURS_GREEN_MAX,
  STAFFER_STAT_CHART_BOTTOM_PERCENT,
  STAFFER_STAT_GOAL_LINE_PERCENT,
  SUBMITS_MONTH_GREEN_MIN,
  SUBMITS_MONTH_ORANGE_MIN,
  SUBMITS_WEEK_GREEN_MIN,
  SUBMITS_WEEK_ORANGE_MIN,
  screenTimeTier,
} from "@/lib/constants";
import { GoalPeriod, PerformanceTier, StafferStatKind } from "@/types";

// The top of a time-to-screen chart: this many hours reaches the card's top
// border, so four times the goal never reads as nearly on target. Worse times
// keep climbing past it and get clipped, which is the point — you can see the
// bar has run off the card. Capped only so the DOM never gets a silly number.
const SCREEN_HOURS_CHART_MAX = 48;
const SCREEN_CHART_MAX_OVERSHOOT = 4;

const submitThresholds = (
  period: GoalPeriod,
): { greenMin: number; orangeMin: number } =>
  period === GoalPeriod.MONTH
    ? { greenMin: SUBMITS_MONTH_GREEN_MIN, orangeMin: SUBMITS_MONTH_ORANGE_MIN }
    : { greenMin: SUBMITS_WEEK_GREEN_MIN, orangeMin: SUBMITS_WEEK_ORANGE_MIN };

export const dashboardStafferStats = {
  isTimeToScreen: (kind: StafferStatKind): boolean =>
    kind === StafferStatKind.TIME_TO_SCREEN,

  formatValue: (kind: StafferStatKind, value: number | null): string => {
    if (value === null) return "—";
    if (kind === StafferStatKind.TIME_TO_SCREEN) return `${value.toString()}h`;
    return value.toString();
  },

  /** The submit count a staffer is expected to hit in the period. */
  submitGoal: (period: GoalPeriod): number => submitThresholds(period).greenMin,

  /** Grades a submit count against the period's flat goal. */
  submitTier: (value: number, period: GoalPeriod): PerformanceTier => {
    const { greenMin, orangeMin } = submitThresholds(period);
    if (value >= greenMin) return PerformanceTier.GREEN;
    if (value >= orangeMin) return PerformanceTier.ORANGE;
    return PerformanceTier.RED;
  },

  tierFor: (
    kind: StafferStatKind,
    value: number,
    period: GoalPeriod,
  ): PerformanceTier =>
    kind === StafferStatKind.TIME_TO_SCREEN
      ? screenTimeTier(value)
      : dashboardStafferStats.submitTier(value, period),

  /**
   * Where a time to first screen sits, as a percentage down from the chart
   * area's top. At-goal times spread between the bottom and the goal line.
   * Slower times run from the goal line to `topPercent` — the card's own top
   * border, which represents SCREEN_HOURS_CHART_MAX — on a fixed scale, so the
   * height always means the same number of hours. Past that it pins to the top.
   */
  screenChartPercent: (hours: number, topPercent: number): number => {
    if (hours <= SCREEN_HOURS_GREEN_MAX) {
      return (
        STAFFER_STAT_CHART_BOTTOM_PERCENT -
        (hours / SCREEN_HOURS_GREEN_MAX) *
          (STAFFER_STAT_CHART_BOTTOM_PERCENT - STAFFER_STAT_GOAL_LINE_PERCENT)
      );
    }
    const overGoalRatio = Math.min(
      (hours - SCREEN_HOURS_GREEN_MAX) /
        (SCREEN_HOURS_CHART_MAX - SCREEN_HOURS_GREEN_MAX),
      SCREEN_CHART_MAX_OVERSHOOT,
    );
    return (
      STAFFER_STAT_GOAL_LINE_PERCENT -
      overGoalRatio * (STAFFER_STAT_GOAL_LINE_PERCENT - topPercent)
    );
  },
};
